import React from "react";
import {
	Popover,
	Switch,
	Checkbox,
	Button,
	Select,
	Divider,
	Popconfirm
} from "antd";
import ReminderIcon from "../../../../../Assets/Others/EmailThread/ReminderIcon.svg";
import { useSelector } from "react-redux";
import {
	Document,
	DocumentAutoReminder,
	EditDocumentRequest,
	DocumentStatus
} from "@/Contracts/Documents";
import useDispatch from "../../../../Utils/useDispatch";
import DocumentsActionCreators from "../../../../../Actions/Documents";
import { ChildEntityHydrationParams } from "@/Contracts/Utils";
import { GlobalState } from "@/Reducers/interface";

const DocumentReminder = () => {
	const dispatch = useDispatch();

	const document = useSelector(
		(state: GlobalState) =>
			state.documents.currentActiveDocument || new Document()
	);

	const {
		auto_email_reminder,
		first_reminder_in,
		repeat_email_reminder,
		repeat_reminder_in
	} = document?.document_email_reminder_details || new DocumentAutoReminder();

	const updateDocumentReminderData = async (
		data: Partial<DocumentAutoReminder>
	) => {
		if (!document) {
			throw new Error("Document not found");
		}
		const { document_email_reminder_details } = document;
		if (!document_email_reminder_details) {
			throw new Error("Invalid document expiry details");
		}
		try {
			await dispatch(
				DocumentsActionCreators.updateCurrentDocument(
					new EditDocumentRequest(
						{
							...document,

							document_email_reminder_details: {
								...document_email_reminder_details,
								...data
							}
						},
						new Array<ChildEntityHydrationParams>()
					)
				)
			);
		} catch (err) {
			console.error("error in updating the document auto reminder");
		}
	};

	const createDocumentAutoReminderData = () => {
		if (!document) {
			throw new Error("Document not found");
		}
		try {
			dispatch(
				DocumentsActionCreators.updateCurrentDocument(
					new EditDocumentRequest(
						{
							...document,
							document_email_reminder_details: {
								...new DocumentAutoReminder(),
								auto_email_reminder: true
							}
						},
						new Array<ChildEntityHydrationParams>()
					)
				)
			);
		} catch (err) {
			console.error("Error in enabling the auto reminder for document");
		}
	};

	const deleteDocumentAutoReminderData = () => {
		if (!document) {
			throw new Error("Document not found");
		}
		try {
			dispatch(
				DocumentsActionCreators.updateCurrentDocument(
					new EditDocumentRequest(
						{
							...document,
							document_email_reminder_details: {
								...new DocumentAutoReminder(),
								auto_email_reminder: false
							}
						},
						new Array<ChildEntityHydrationParams>()
					)
				)
			);
		} catch (err) {
			console.error("error in disabling auto reminder", err);
		}
	};

	const handleFirstReminderDayChange = (value: number) => {
		updateDocumentReminderData({
			first_reminder_in: value
		});
	};

	const handleRepeatReminderDayChange = (value: number) => {
		updateDocumentReminderData({
			repeat_reminder_in: value
		});
	};

	const handleRepeatReminderDayCheckboxChange = () => {
		if (repeat_email_reminder) {
			updateDocumentReminderData({
				repeat_email_reminder: false
			});
			return;
		}

		updateDocumentReminderData({
			repeat_email_reminder: true
		});
	};

	const handleAutoReminderSwitchChange = () => {
		if (auto_email_reminder) {
			deleteDocumentAutoReminderData();
		} else {
			createDocumentAutoReminderData();
		}
	};

	const handleDisableReminder = () => {
		updateDocumentReminderData({
			auto_email_reminder: false
		});
	};

	const isDraft = document?.status === DocumentStatus.Draft;

	return (
		<Popover
			placement="topLeft"
			overlayClassName="document-autoreminder-popover"
			content={
				<div className="document-autoreminder-settings-wrapper">
					<label className={`autoreminder-switch`}>
						<span>Auto Email Reminder </span>
						&nbsp;
						{document?.status === DocumentStatus.Draft ? (
							<Switch
								checked={auto_email_reminder}
								size="small"
								onChange={handleAutoReminderSwitchChange}
							/>
						) : (
							<Popconfirm
								disabled={!auto_email_reminder}
								title="Once you turn off the reminders for document you won’t be able to turn it back on. Are you sure you want to turn the reminders off ?"
								onConfirm={handleDisableReminder}
								okText="Turn off Reminders"
								cancelText="Cancel"
							>
								<Switch
									disabled={!auto_email_reminder}
									checked={auto_email_reminder}
									size="small"
								/>
							</Popconfirm>
						)}
					</label>
					<span className="autoreminder-info">
						Automatically send email reminders to the recipients who
						have not yet approved the document
					</span>
					<label
						className={`autoreminder-select-wrapper ${
							auto_email_reminder ? "visible" : ""
						}`}
					>
						<span>First reminder in </span>
						<Select<number>
							getPopupContainer={trigger => trigger.parentNode}
							disabled={isDraft ? !auto_email_reminder : true}
							value={first_reminder_in}
							onChange={value =>
								handleFirstReminderDayChange(value)
							}
						>
							<Select.Option value={1}>1 day</Select.Option>
							<Select.Option value={3}>3 days</Select.Option>
							<Select.Option value={5}>5 days</Select.Option>
							<Select.Option value={7}>7 days</Select.Option>
							<Select.Option value={10}>10 days</Select.Option>
							<Select.Option value={30}>30 days</Select.Option>
						</Select>
					</label>
					<Divider />
					<span
						className={`autoreminder-checkbox-wrapper ${
							auto_email_reminder ? "visible" : ""
						}`}
					>
						<Checkbox
							checked={repeat_email_reminder}
							disabled={isDraft ? !auto_email_reminder : true}
							onChange={handleRepeatReminderDayCheckboxChange}
						/>
						<span>Repeat </span>
						<Select<number>
							getPopupContainer={trigger => trigger.parentNode}
							disabled={isDraft ? !repeat_email_reminder : true}
							value={repeat_reminder_in}
							onChange={value =>
								handleRepeatReminderDayChange(value)
							}
						>
							<Select.Option value={1}>every 1 day</Select.Option>
							<Select.Option value={3}>
								every 3 days
							</Select.Option>
							<Select.Option value={5}>
								every 5 days
							</Select.Option>
							<Select.Option value={7}>
								every 7 days
							</Select.Option>
							<Select.Option value={10}>
								every 10 days
							</Select.Option>
							<Select.Option value={30}>
								every 30 days
							</Select.Option>
						</Select>
						<span>until completed or expired</span>
					</span>
				</div>
			}
			trigger="click"
		>
			<div
				className={`footer-activities-text ${
					auto_email_reminder ? "icon-active" : ""
				}`}
			>
				<Button type="link">
					<ReminderIcon /> Reminder
				</Button>
			</div>
		</Popover>
	);
};

export default DocumentReminder;
